export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  status: string;
}

export interface AuthWorkspace {
  id: string;
  type: 'personal' | 'company' | 'group';
  name: string;
  owner_user_id: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer' | 'auditor';
}

export interface AuthActor {
  user: AuthUser;
  workspace: AuthWorkspace | null;
}

export interface StartLoginResult {
  email: string;
  expires_at: string;
  email_mode: 'debug_modal' | 'resend' | 'disabled';
  debug_login_url?: string;
}

export interface WorkspaceListResult {
  active_workspace_id: string | null;
  workspaces: AuthWorkspace[];
}

function apiBaseUrl(): string {
  const base = (import.meta.env.VITE_API_BASE_URL ?? '').trim();
  return (base || '/api').replace(/\/+$/, '');
}

function apiPath(path: string): string {
  return `${apiBaseUrl()}/${path.replace(/^\/+/, '')}`;
}

function authPath(path: string): string {
  return apiPath(`auth/${path.replace(/^\/+/, '')}`);
}

async function requestUrl<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error ?? `認証APIに失敗しました: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

async function authRequest<T>(path: string, init?: RequestInit): Promise<T> {
  return requestUrl<T>(authPath(path), init);
}

async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  return requestUrl<T>(apiPath(path), init);
}

export function webAuthEnabled(): boolean {
  return import.meta.env.VITE_WEB_AUTH_ENABLED === 'true';
}

export function startLogin(email: string): Promise<StartLoginResult> {
  return authRequest<StartLoginResult>('start', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export function verifyLoginToken(token: string): Promise<AuthActor> {
  return authRequest<AuthActor>(`verify?token=${encodeURIComponent(token)}`, {
    method: 'GET',
  });
}

export function loadCurrentActor(): Promise<AuthActor> {
  return authRequest<AuthActor>('me');
}

export function logout(): Promise<{ ok: true }> {
  return authRequest<{ ok: true }>('logout', { method: 'POST' });
}

export function listWorkspaces(): Promise<WorkspaceListResult> {
  return apiRequest<WorkspaceListResult>('workspaces');
}

export function switchActiveWorkspace(workspaceId: string): Promise<AuthActor> {
  return apiRequest<AuthActor>('workspaces/active', {
    method: 'PUT',
    body: JSON.stringify({ workspace_id: workspaceId }),
  });
}
